"use client"

import { Suspense, useEffect, useState } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import { Loader2, MonitorPlay } from "lucide-react"
import serviceAppsService from "@/services/service-apps"
import { useAuth } from "@/hooks/use-auth"
import { toast } from "sonner"
import { lmsDestination } from "@/lib/lms-destination"

function GoToLms() {
    const [message, setMessage] = useState("Opening LMS…")
    const { isAuthenticated, isLoading } = useAuth()
    const router = useRouter()
    const to = lmsDestination(useSearchParams().get("to"))

    useEffect(() => {
        if (isLoading) return
        if (!isAuthenticated) {
            router.replace(`/login?next=${encodeURIComponent(`/go-to-lms?to=${to}`)}`)
            return
        }

        let cancelled = false

        const redirect = async () => {
            try {
                setMessage("Connecting your account…")
                const { client_id } = await serviceAppsService.lookupByName("learning_management_system")
                const { callback_url } = await serviceAppsService.initiateSession(client_id, to)

                if (!cancelled) {
                    window.location.replace(callback_url)
                }
            } catch (err: any) {
                const detail = err?.response?.data?.detail ?? "Could not connect to LMS. Please try again."
                toast.error(detail)
                setMessage(detail)
            }
        }

        redirect()

        return () => {
            cancelled = true
        }
    }, [isAuthenticated, isLoading, router, to])

    return (
        <main className="min-h-screen bg-background grid place-items-center p-6">
            <div className="w-full max-w-md rounded-3xl border border-border bg-card p-8 text-center shadow-xl">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                    <MonitorPlay className="h-7 w-7" />
                </div>
                <h1 className="text-2xl font-bold tracking-tight">Opening LMS</h1>
                <p className="mt-2 text-sm text-muted-foreground">{message}</p>
                <div className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Please wait while we set up your session.
                </div>
            </div>
        </main>
    )
}

export default function GoToLmsPage() {
    // useSearchParams needs a Suspense boundary so the page can still be prerendered.
    return (
        <Suspense fallback={null}>
            <GoToLms />
        </Suspense>
    )
}
